import { PostMessageOption, SystemParam } from '../../types';
import {
  initDefaultBridge,
  postJsMessage,
  registerSpecificNotificationHandler,
  unregisterSpecificNotificationHandler,
} from '../../bridge';
import API from '../../api';
import { NativeAPIResponseMessage } from '../../utils/type';
import { ERROR_CODE, genError, isAvailable } from '../utils';
import { safeInvokeNoArg } from '../../utils/function';
import {
  EmojiReactionSkinTone,
  refreshThemeSkinInternalListener,
} from 'jssdk/theme';

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * platform.getSystemParam
 *
 * [PLATFORM]
 * mac        5.17.0+
 * windows    5.17.0+
 * linux      5.17.0+
 * ios        5.17.0+
 * android    5.17.0+
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
export async function getSystemParam(
  options: { preferCache: boolean } = { preferCache: true }
) {
  if (!('unifySdk' in window)) {
    initDefaultBridge();
  }
  refreshThemeSkinInternalListener();
  if (options?.preferCache) {
    if (window.unifyConfig?.systemParam) {
      return window.unifyConfig.systemParam;
    } else if (typeof window.android_config?.getSystemParam === 'function') {
      return JSON.parse(window.android_config.getSystemParam()) as SystemParam;
    }
  }
  const response: NativeAPIResponseMessage<SystemParam> = await postJsMessage(
    API.getSystemParam,
    {},
    { timeout: 3 }
  );
  if (response && !response.returnCode) {
    if (window.unifyConfig) {
      window.unifyConfig.systemParam = response.result;
    }
    return response.result;
  } else {
    return Promise.reject(
      genError({
        errorCode: ERROR_CODE.WEBVIEW_MISSING_SYSTEM_PARAM,
        apiName: API.getSystemParam,
        message: 'Failed to get getSystemParam from webview',
      })
    );
  }
}

type TimeZoneInfo = {
  timezone: string;
  timeZoneOffset: string;
};

const TimeZoneInfoNotification = 'UnifyNotify_OnTimeZoneChange';
export type TimeZoneInfoChangeCallback = (data: TimeZoneInfo) => void;
let onTimeZoneInfoChangeFns: TimeZoneInfoChangeCallback[] = [];
let isTimeZoneInfoChangeRegistered = false;
const onTimeZoneInfoChangeHandler = (
  message: NativeAPIResponseMessage<TimeZoneInfo>
) => {
  const { returnCode, result } = message;
  if (returnCode === 0) {
    const { timezone, timeZoneOffset } = result;
    onTimeZoneInfoChangeFns.forEach((fn) => {
      safeInvokeNoArg(() => fn({ timezone, timeZoneOffset }));
    });
    if (window.unifyConfig?.systemParam) {
      window.unifyConfig.systemParam.timeZone = timezone;
      window.unifyConfig.systemParam.timeZoneOffset = timeZoneOffset;
    }
  } else {
    console.warn('onTimeZoneInfoChangeHandler failed', message);
  }
};

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * platform.onTimeZoneChange
 *
 * [PLATFORM]
 * mac        6.1.15+
 * windows    6.1.15+
 * linux      NotSupport
 * ios        6.1.15+
 * android    6.1.15+
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
export async function onTimeZoneChange(
  onTimezoneInfoChangeFn: TimeZoneInfoChangeCallback
) {
  await isAvailable('platform.onTimeZoneChange');
  onTimeZoneInfoChangeFns.push(onTimezoneInfoChangeFn);

  if (!isTimeZoneInfoChangeRegistered) {
    isTimeZoneInfoChangeRegistered = true;
    registerSpecificNotificationHandler(
      TimeZoneInfoNotification,
      onTimeZoneInfoChangeHandler
    );
  }
  return Promise.resolve(); // keep back compatible
}

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * platform.offTimeZoneChange
 *
 * [PLATFORM]
 * mac        6.1.15+
 * windows    6.1.15+
 * linux      NotSupport
 * ios        6.1.15+
 * android    6.1.15+
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
export async function offTimeZoneChange(
  onTimezoneInfoChangeFn: TimeZoneInfoChangeCallback
) {
  await isAvailable('platform.offTimeZoneChange');
  onTimeZoneInfoChangeFns = onTimeZoneInfoChangeFns.filter(
    (fn) => fn !== onTimezoneInfoChangeFn
  );
  if (onTimeZoneInfoChangeFns.length === 0) {
    unregisterSpecificNotificationHandler(
      TimeZoneInfoNotification,
      onTimeZoneInfoChangeHandler
    );
    isTimeZoneInfoChangeRegistered = false;
  }
  return Promise.resolve(); // keep back compatible
}

type TimeFormatInfo = {
  is24TimeFormat: boolean;
};
export type onTimeFormatChangeCallback = (
  data: NativeAPIResponseMessage<TimeFormatInfo>
) => void;
let isTimeFormatChangeRegistered = false;
let onTimeFormatChangeCallbackFns: onTimeFormatChangeCallback[] = [];
const TimeFormatChangeNotification = 'UnifyNotify_OnTimeFormatChange';
const onTimeFormatChangeHandler = (
  message: NativeAPIResponseMessage<TimeFormatInfo>
) => {
  const { returnCode, result } = message;
  if (returnCode === 0) {
    const { is24TimeFormat } = result;
    onTimeFormatChangeCallbackFns.forEach((fn) => {
      safeInvokeNoArg(() => fn(message));
    });
    if (window.unifyConfig?.systemParam) {
      window.unifyConfig.systemParam.is24TimeFormat = is24TimeFormat;
    }
  } else {
    console.warn('onTimeFormatChangeHandler failed', message);
  }
};

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * platform.onTimeFormatChange
 *
 * [PLATFORM]
 * mac        6.2.0+
 * windows    6.2.0+
 * linux      NotSupport
 * ios        6.2.0+
 * android    6.2.0+
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
export async function onTimeFormatChange(
  onTimeFormatChangeFn: onTimeFormatChangeCallback
) {
  await isAvailable('platform.onTimeFormatChange');
  onTimeFormatChangeCallbackFns.push(onTimeFormatChangeFn);

  if (!isTimeFormatChangeRegistered) {
    isTimeFormatChangeRegistered = true;
    registerSpecificNotificationHandler(
      TimeFormatChangeNotification,
      onTimeFormatChangeHandler
    );
  }
  return Promise.resolve();
}

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * platform.offTimeFormatChange
 *
 * [PLATFORM]
 * mac        6.2.0+
 * windows    6.2.0+
 * linux      NotSupport
 * ios        6.2.0+
 * android    6.2.0+
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
export async function offTimeFormatChange(
  onTimeFormatChangeFn: onTimeFormatChangeCallback
) {
  await isAvailable('platform.offTimeFormatChange');
  onTimeFormatChangeCallbackFns = onTimeFormatChangeCallbackFns.filter(
    (fn) => fn !== onTimeFormatChangeFn
  );
  if (onTimeFormatChangeCallbackFns.length === 0) {
    unregisterSpecificNotificationHandler(
      TimeFormatChangeNotification,
      onTimeFormatChangeHandler
    );
    isTimeFormatChangeRegistered = false;
  }
  return Promise.resolve();
}

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * platform.getClientSetting
 *
 * [PLATFORM]
 * mac        6.4.0+
 * windows    6.4.0+
 * linux      6.4.0+
 * ios        6.4.0+
 * android    6.4.0+
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
export async function getClientSetting<V extends ValueType = 'string'>(
  key: string,
  valueType?: V,
  options?: PostMessageOption
) {
  await isAvailable('platform.getClientSetting');
  if (!valueType) {
    (valueType as unknown as string) = 'string';
  }
  return postJsMessage<
    GetClientSetting,
    ClientSetting<ValueTypeToReturnType<V>>
  >(API.getClientSetting, { key, valueType }, options);
}

type ValueType = 'string' | 'bool' | 'int';
type ValueTypeToReturnType<T extends ValueType> = T extends 'string'
  ? string
  : T extends 'bool'
    ? boolean
    : T extends 'int'
      ? number
      : never;
type GetClientSetting = {
  key: string;
  valueType?: ValueType;
};
type ClientSetting<V> = {
  key: string;
  value: V;
};

export type RawSetting = {
  'accessibility.chatDisplaySize'?: string;
  'theme.emojiReactionSkin'?: string;
};

export type Setting = {
  'accessibility.chatDisplaySize'?: number;
  'theme.emojiReactionSkin'?: EmojiReactionSkinTone;
};

export type OnRawClientSettingChange = (
  notification: NativeAPIResponseMessage<RawSetting>
) => void;

export type OnClientSettingChange = (
  notification: NativeAPIResponseMessage<Setting>
) => void;

function formatClientSettingData(change: RawSetting): Setting {
  const setting: Setting = {};
  Object.keys(change).forEach((key) => {
    switch (key) {
      case 'accessibility.chatDisplaySize':
        setting['accessibility.chatDisplaySize'] = parseInt(change[key]!, 10);
        break;

      case 'theme.emojiReactionSkin':
        setting['theme.emojiReactionSkin'] = parseInt(
          change[key]!,
          10
        ) as EmojiReactionSkinTone;
        break;

      default:
        break;
    }
  });
  return setting;
}

const settingChangedCBWrapperMap = new WeakMap<
  OnClientSettingChange,
  OnRawClientSettingChange
>();
/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * platform.onClientSettingChanged
 *
 * [PLATFORM]
 * mac        6.6.0
 * windows    6.6.0
 * linux      6.6.0
 * ios        NotSupport
 * android    NotSupport
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
export function onClientSettingChanged(cb: OnClientSettingChange) {
  if (!settingChangedCBWrapperMap.has(cb)) {
    const wrappedCb = (notification: NativeAPIResponseMessage<RawSetting>) => {
      if (notification.returnCode === 0) {
        const normalizedNotification = {
          ...notification,
          result: formatClientSettingData(notification.result),
        };
        cb(normalizedNotification);
      } else {
        cb(notification as NativeAPIResponseMessage<Setting>);
      }
    };
    settingChangedCBWrapperMap.set(cb, wrappedCb);
  }

  const wrappedCb = settingChangedCBWrapperMap.get(cb)!;
  registerSpecificNotificationHandler(
    'UnifyNotify_OnClientSettingChanged',
    wrappedCb
  );
}

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * platform.offClientSettingChanged
 *
 * [PLATFORM]
 * mac        6.6.0
 * windows    6.6.0
 * linux      6.6.0
 * ios        NotSupport
 * android    NotSupport
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
export function offClientSettingChanged(cb: OnClientSettingChange) {
  const wrappedCb = settingChangedCBWrapperMap.get(cb);
  if (wrappedCb) {
    unregisterSpecificNotificationHandler(
      'UnifyNotify_OnClientSettingChanged',
      wrappedCb
    );
    settingChangedCBWrapperMap.delete(cb);
  }
}

export default {
  getSystemParam,
  onTimeZoneChange,
  offTimeZoneChange,
  onTimeFormatChange,
  offTimeFormatChange,
  getClientSetting,
  onClientSettingChanged,
  offClientSettingChanged,
};

export type { TimeZoneInfo, ClientSetting, ValueType };
